/* eslint-disable react-refresh/only-export-components -- This file exports
   a route MANIFEST, not components. Its screens are defined inside a
   factory so they can close over the API client, which puts them beyond
   Fast Refresh's reach whatever this file exports. Editing a screen
   refreshes normally; editing this file re-mounts the router, which is
   correct — the route table changed. */
/**
 * What this feature contributes to the application's route tree.
 *
 * A factory taking the API client rather than a static array, because the
 * screens need it and the alternative — a module-level singleton — makes the
 * package untestable and impossible to mount twice.
 *
 * Paths are exported as constants so nothing links to a route by typing its
 * string. A typo in a link is a 404 nobody notices until a user does.
 */

import { useNavigate } from "@tanstack/react-router";
import type { ApiClient } from "@hardauto/core-api";
import { safeReturnTo, type FeatureRoutes } from "@hardauto/core-router";

import { AccountScreen } from "./screens/AccountScreen";
import {
  ForgotPasswordScreen,
  RegisterScreen,
  ResetPasswordScreen,
  VerifyEmailScreen,
} from "./screens/RecoveryScreens";
import { SignInScreen } from "./screens/SignInScreen";
import { AuthCallbackScreen } from "./social/AuthCallbackScreen";
import "./i18n";

export const IAM_PATHS = {
  signIn: "/sign-in",
  /**
   * Where every provider sends the browser back.
   *
   * One path for all three, and it is not a preference: each provider matches
   * the redirect URI exactly against what was registered in its console, so a
   * per-provider path would mean registering one per provider — and a
   * per-tenant path, one per tenant, forever. The `state` says which provider
   * and which tenant a callback belongs to.
   */
  authCallback: "/auth/callback",
  register: "/register",
  forgotPassword: "/forgot-password",
  resetPassword: "/reset-password",
  verifyEmail: "/verify-email",
  account: "/account",
} as const;

/**
 * Read a single-use token out of the query string.
 *
 * `window.location` rather than the router's typed search params, because
 * these routes are reached from an e-mail link — the token is present on the
 * very first render, before the router has parsed anything, and the screens
 * act on it immediately.
 */
function tokenFromQuery(): string {
  if (typeof window === "undefined") return "";
  return new URLSearchParams(window.location.search).get("token") ?? "";
}

/** Whether the link is an invitation (`&invitation=1`) rather than a password reset. */
function invitationFromQuery(): boolean {
  if (typeof window === "undefined") return false;
  return new URLSearchParams(window.location.search).get("invitation") === "1";
}

/** What an application may change about IAM's own routes. */
export interface IamRouteOptions {
  /**
   * Where "create an account" on the sign-in page points.
   *
   * Defaults to IAM's own self-registration. An application whose accounts
   * are not self-service points it elsewhere — a B2B portal sends a visitor
   * to an account-opening *request*, because an account there is a company,
   * an ICE and a credit limit, and somebody decides.
   */
  registerHref?: string;
  /**
   * Mount IAM's self-registration route at all.
   *
   * `false` removes the page as well as the link. Leaving the route mounted
   * while pointing the link elsewhere would leave a working sign-up form one
   * URL away — and in a portal whose accounts must be linked to a company,
   * that form produces a user who signs in to "compte non rattaché".
   */
  selfRegistration?: boolean;
}

export function iamRoutes(client: ApiClient, options: IamRouteOptions = {}): FeatureRoutes {
  const registerHref = options.registerHref ?? IAM_PATHS.register;
  const selfRegistration = options.selfRegistration ?? true;

  function SignIn() {
    const navigate = useNavigate();
    return (
      <SignInScreen
        onSignedIn={() => {
          // Back to wherever they were headed before the session gate
          // intercepted them — validated, so a crafted `returnTo` cannot turn
          // this page into an open redirect.
          const target = safeReturnTo(window.location.search);
          void navigate({ to: target, replace: true });
        }}
        registerHref={registerHref}
        forgotPasswordHref={IAM_PATHS.forgotPassword}
        client={client}
      />
    );
  }

  function AuthCallback() {
    const navigate = useNavigate();
    return (
      <AuthCallbackScreen
        onSignedIn={() => {
          void navigate({ to: safeReturnTo(window.location.search), replace: true });
        }}
        signInHref={IAM_PATHS.signIn}
      />
    );
  }

  function Register() {
    return <RegisterScreen client={client} signInHref={IAM_PATHS.signIn} />;
  }

  function ForgotPassword() {
    return <ForgotPasswordScreen client={client} signInHref={IAM_PATHS.signIn} />;
  }

  function ResetPassword() {
    const navigate = useNavigate();
    return (
      <ResetPasswordScreen
        client={client}
        token={tokenFromQuery()}
        invitation={invitationFromQuery()}
        onDone={() => {
          void navigate({ to: IAM_PATHS.signIn, replace: true });
        }}
      />
    );
  }

  function VerifyEmail() {
    return (
      <VerifyEmailScreen client={client} token={tokenFromQuery()} signInHref={IAM_PATHS.signIn} />
    );
  }

  function Account() {
    return <AccountScreen client={client} />;
  }

  return {
    id: "iam",
    routes: [
      {
        path: IAM_PATHS.signIn,
        component: SignIn,
        layout: "public",
        // Signed-in users are sent home rather than shown a form they cannot
        // usefully submit, which is what makes a bookmarked /sign-in behave.
        requirement: { anonymousOnly: true },
      },
      {
        path: IAM_PATHS.authCallback,
        component: AuthCallback,
        layout: "public",
        // NOT `anonymousOnly`, unlike the sign-in form.
        //
        // The callback runs while the session is being established, and a
        // gate that redirected an already-authenticated visitor away would
        // race the very sign-in it is meant to complete — which shows up as
        // an intermittent failure that only appears on a fast network.
      },
      ...(selfRegistration
        ? [
            {
              path: IAM_PATHS.register,
              component: Register,
              layout: "public" as const,
              requirement: { anonymousOnly: true },
            },
          ]
        : []),
      { path: IAM_PATHS.forgotPassword, component: ForgotPassword, layout: "public" },
      // NOT `anonymousOnly`: a signed-in user following a reset link from their
      // e-mail must still be able to complete it. Bouncing them home would
      // consume the link for nothing.
      { path: IAM_PATHS.resetPassword, component: ResetPassword, layout: "public" },
      { path: IAM_PATHS.verifyEmail, component: VerifyEmail, layout: "public" },
      {
        path: IAM_PATHS.account,
        component: Account,
        layout: "app",
        requirement: { authenticated: true },
        nav: { labelKey: "iam:account.title", section: "account", order: 10 },
      },
    ],
  };
}
